import React, { useEffect, useState } from "react";
import { api, PROGRAM_LABELS, formatErr } from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  LineChart, Line, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";

const STATUS_COLORS = {
  paid: "bg-[#D4E4D3] text-[#2F4F2F]",
  pending: "bg-[#F9E8C0] text-[#7A5C1F]",
  failed: "bg-[#F2D6D3] text-[#8C3A3A]",
  refunded: "bg-[#E2E0DD] text-[#5C5955]",
};
const PIE = ["#E07A5F", "#7A9E7E", "#D9B26F", "#B07156", "#8AA399", "#C98B6B"];

function StatCard({ label, value, hint, testid }) {
  return (
    <div className="soft-card !p-6" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-[#7E736E]">{label}</div>
      <div className="font-serif text-3xl text-[#2D2422] mt-2">{value}</div>
      {hint && <div className="text-xs text-[#7E736E] mt-2">{hint}</div>}
    </div>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <div className="soft-card !p-6" data-testid={testid}>
      <div className="font-serif text-xl mb-4">{title}</div>
      <div className="h-60">
        <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/admin/dashboard/stats").then(({ data }) => setStats(data)).catch(e => setErr(formatErr(e)));
  }, []);
  if (err) return <div className="text-sm text-red-700">{err}</div>;
  if (!stats) return <div className="text-sm text-[#7E736E]">Loading…</div>;

  const progData = Object.entries(stats.by_program).map(([k, v]) => ({ name: (PROGRAM_LABELS[k] || k).split(" ")[0], count: v }));
  const statusData = Object.entries(stats.by_status || {}).map(([k, v]) => ({ name: k, value: v }));
  const marketData = [
    { name: "India (.in)", value: stats.by_market?.in || 0 },
    { name: "Global (.com)", value: stats.by_market?.com || 0 },
  ];
  const sourceData = (stats.by_source || []).map(s => ({ name: s.source, count: s.count }));
  const funnelData = [{ name: "Total", count: stats.total }, { name: "Paid", count: stats.paid }];

  return (
    <div className="space-y-8 fade-up" data-testid="admin-dashboard">
      <div>
        <h1 className="font-serif text-4xl">Dashboard</h1>
        <p className="text-[#7E736E] text-sm">Registrations, revenue, channel routing and recent activity at a glance.</p>
      </div>

      {/* Row 1 — KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
        <StatCard testid="stat-total" label="Total Registrations" value={stats.total} />
        <StatCard testid="stat-paid" label="Paid" value={stats.paid} hint={`${stats.conversion}% conversion`} />
        <StatCard testid="stat-refunded" label="Refunded" value={stats.refunded || 0} />
        <StatCard testid="stat-inr" label="Revenue (INR)" value={`₹${(stats.revenue_inr || 0).toLocaleString("en-IN")}`} hint=".in · Razorpay" />
        <StatCard testid="stat-usd" label="Revenue (USD)" value={`$${(stats.revenue_usd || 0).toLocaleString("en-US")}`} hint=".com · PayPal" />
      </div>

      {/* Row 2 — Charts */}
      <div className="grid lg:grid-cols-2 gap-5">
        <ChartCard title="Registrations · last 14 days" testid="chart-registrations">
          <LineChart data={stats.registrations_by_day}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EBE5D9" />
            <XAxis dataKey="date" tickFormatter={d => d.slice(5)} stroke="#7E736E" />
            <YAxis allowDecimals={false} stroke="#7E736E" />
            <Tooltip />
            <Line dataKey="count" stroke="#E07A5F" strokeWidth={2} dot={false} />
          </LineChart>
        </ChartCard>
        <ChartCard title="Bookings by program" testid="chart-programs">
          <BarChart data={progData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EBE5D9" />
            <XAxis dataKey="name" stroke="#7E736E" /><YAxis allowDecimals={false} stroke="#7E736E" /><Tooltip />
            <Bar dataKey="count" fill="#E07A5F" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ChartCard>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <ChartCard title="Payment status" testid="chart-status">
          <PieChart>
            <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
              {statusData.map((e, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
            </Pie>
            <Tooltip /><Legend />
          </PieChart>
        </ChartCard>
        <ChartCard title="Market split (.in vs .com)" testid="chart-market">
          <PieChart>
            <Pie data={marketData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
              <Cell fill="#7A9E7E" /><Cell fill="#D9B26F" />
            </Pie>
            <Tooltip /><Legend />
          </PieChart>
        </ChartCard>
        <ChartCard title="Revenue by gateway" testid="chart-revenue-gateway">
          <BarChart data={stats.revenue_by_gateway || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EBE5D9" />
            <XAxis dataKey="name" stroke="#7E736E" tick={{ fontSize: 11 }} /><YAxis stroke="#7E736E" /><Tooltip />
            <Bar dataKey="amount" fill="#B07156" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ChartCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <ChartCard title="Lead sources" testid="chart-sources">
          <BarChart data={sourceData} layout="vertical" margin={{ left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EBE5D9" />
            <XAxis type="number" allowDecimals={false} stroke="#7E736E" />
            <YAxis type="category" dataKey="name" stroke="#7E736E" width={90} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="count" fill="#8AA399" radius={[0, 8, 8, 0]} />
          </BarChart>
        </ChartCard>
        <ChartCard title="Conversion funnel" testid="chart-funnel">
          <BarChart data={funnelData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EBE5D9" />
            <XAxis dataKey="name" stroke="#7E736E" /><YAxis allowDecimals={false} stroke="#7E736E" /><Tooltip />
            <Bar dataKey="count" radius={[8, 8, 0, 0]}>
              <Cell fill="#C98B6B" /><Cell fill="#7A9E7E" />
            </Bar>
          </BarChart>
        </ChartCard>
      </div>

      {/* Row 3 — Recent bookings */}
      <div className="soft-card !p-6">
        <div className="font-serif text-2xl mb-4">Recent Bookings</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recent-bookings-table">
            <thead><tr className="text-left text-[#7E736E] text-xs uppercase tracking-wider">
              <th className="py-2">Name</th><th>Program</th><th>Country</th><th>Amount</th><th>Status</th><th>Registered</th>
            </tr></thead>
            <tbody>
              {stats.recent.map(b => (
                <tr key={b.id} className="border-t border-[#EBE5D9]">
                  <td className="py-3">{b.full_name}<div className="text-xs text-[#7E736E]">{b.email}</div></td>
                  <td>{PROGRAM_LABELS[b.program_type] || b.program_type}</td>
                  <td>{b.country}</td>
                  <td>{b.currency === "INR" ? "₹" : "$"}{b.amount}</td>
                  <td><span className={`badge-soft ${STATUS_COLORS[b.payment_status] || ""}`}>{b.payment_status}</span></td>
                  <td className="text-xs text-[#7E736E]">{(b.registered_at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
              {stats.recent.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-[#7E736E]">No bookings yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
